/**
 * Rendering of small scale screen space shadows.
 * @module
 */
import * as THREE from "three";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import type { DepthNormalPass } from "./DepthNormalPass";
import {
  gtaoAcculumateSlicesGLSL,
  gtaoCommonUniformDefaults,
  gtaoCommonUniformGLSL,
  gtaoHelpersGLSL,
} from "./GTAOShader";
import {
  screenSpaceViewNormalDefineDefaults,
  screenSpaceViewNormalGLSL,
  screenSpaceViewNormalPrepareShader,
  screenSpaceViewNormalUniformDefaults,
  screenSpaceViewPositionDefineDefaults,
  screenSpaceViewPositionGLSL,
  screenSpaceViewPositionPrepareShader,
  screenSpaceViewPositionUniformDefaults,
} from "./screenSpaceHelpersGLSL";
import { UVVertexShader } from "./UVVertexShader";

export interface ScreenSpaceShadowParams {
  shadowSteps: number;
  assumedThickness: number;
  aoSamples: number;
  aoAngleSamples: number;
  aoCosineWeighting: boolean;
  aoIntensity: number;
}

export enum ScreenSpaceShadowPassOutput {
  WriteToScreen = 0,
  ShadowBuffer = 1,
}

export const defaultScreenSpaceShadowParams: ScreenSpaceShadowParams = {
  shadowSteps: 4,
  assumedThickness: 4.0,
  aoSamples: 2,
  aoAngleSamples: 2,
  aoCosineWeighting: false,
  aoIntensity: 0.8,
};

export class ScreenSpaceShadowPass extends EffectComposerPass {
  private _shadowsEnabled: boolean = true;
  private _aoEnabled: boolean = true;

  assumedThickness: number = defaultScreenSpaceShadowParams.assumedThickness;

  shadowBuffer: THREE.WebGLRenderTarget;

  // Random seed to use on the next render. Should be between 0 and 1.
  randomSeed = 0;

  scene: THREE.Scene | undefined;
  camera: THREE.Camera;

  depthNormalPass: DepthNormalPass;

  lightDirection: THREE.Vector3 = new THREE.Vector3(0, 0, 1);

  aoWorldSpaceScale: number = 6.0;
  aoMaxScreenSpaceKernelRadius: number = 0.01;
  aoFalloffMinRelativeRadius: number = 0.3;
  aoFalloffMaxRelativeRadius: number = 1.3;
  aoIntensity: number = 0.8;

  private _shadowSteps: number = defaultScreenSpaceShadowParams.shadowSteps;
  private _aoSamples: number = defaultScreenSpaceShadowParams.aoSamples;
  private _aoAngleSamples: number = defaultScreenSpaceShadowParams.aoAngleSamples;
  private _aoCosineWeighting: boolean = defaultScreenSpaceShadowParams.aoCosineWeighting;
  private _output: ScreenSpaceShadowPassOutput = ScreenSpaceShadowPassOutput.ShadowBuffer;

  private _prevClearColor: THREE.Color;

  private outputMaterial: THREE.ShaderMaterial;
  private fsQuad: FullScreenQuad;

  constructor(scene: THREE.Scene | undefined, camera: THREE.Camera, depthNormalPass: DepthNormalPass) {
    super();

    this.scene = scene;
    this.camera = camera;
    this.depthNormalPass = depthNormalPass;

    this._prevClearColor = new THREE.Color();

    this.shadowBuffer = new THREE.WebGLRenderTarget(256, 256, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGFormat,
      depthBuffer: false,
      stencilBuffer: false,
    });

    this.outputMaterial = this.getOutputMaterial();
    this.fsQuad = new FullScreenQuad(this.outputMaterial);

    this.updateOutputMaterial();
  }

  get shadowsEnabled(): boolean {
    return this._shadowsEnabled;
  }
  set shadowsEnabled(value: boolean) {
    if (this._shadowsEnabled === value) {
      return;
    }
    this._shadowsEnabled = value;
    this.updateOutputMaterial();
  }

  get aoEnabled(): boolean {
    return this._aoEnabled;
  }
  set aoEnabled(value: boolean) {
    if (this._aoEnabled === value) {
      return;
    }
    this._aoEnabled = value;
    this.updateOutputMaterial();
  }

  get shadowSteps(): number {
    return this._shadowSteps;
  }
  set shadowSteps(value: number) {
    if (this._shadowSteps === value) {
      return;
    }
    this._shadowSteps = value;
    this.updateOutputMaterial();
  }

  get aoSamples(): number {
    return this._aoSamples;
  }
  set aoSamples(value: number) {
    if (this._aoSamples === value) {
      return;
    }
    this._aoSamples = value;
    this.updateOutputMaterial();
  }

  get aoAngleSamples(): number {
    return this._aoAngleSamples;
  }
  set aoAngleSamples(value: number) {
    if (this._aoAngleSamples === value) {
      return;
    }
    this._aoAngleSamples = value;
    this.updateOutputMaterial();
  }

  get aoCosineWeighting(): boolean {
    return this._aoCosineWeighting;
  }
  set aoCosineWeighting(value: boolean) {
    if (this._aoCosineWeighting === value) {
      return;
    }
    this._aoCosineWeighting = value;
    this.updateOutputMaterial();
  }

  get output() {
    return this._output;
  }
  set output(value) {
    if (this._output === value) {
      return;
    }
    this._output = value;
    this.updateOutputMaterial();
  }

  dispose() {
    this.shadowBuffer.dispose();
  }

  setSize(width: number, height: number) {
    super.setSize(width, height);
    this.shadowBuffer.setSize(this.depthNormalPass.paddedSize.x, this.depthNormalPass.paddedSize.y);
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    renderer.getClearColor(this._prevClearColor);
    const prevClearAlpha = renderer.getClearAlpha();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setClearColor(new THREE.Color(0, 0, 0), 0);

    const uniforms = this.outputMaterial.uniforms;

    uniforms["randomSeed"].value = this.randomSeed;

    screenSpaceViewPositionPrepareShader(this.outputMaterial, this.camera, this.depthNormalPass);
    screenSpaceViewNormalPrepareShader(this.outputMaterial, this.depthNormalPass);

    uniforms.lightDirection.value.copy(this.lightDirection);
    uniforms.resolution.value.set(this.shadowBuffer.width, this.shadowBuffer.height);

    uniforms.assumedThickness.value = this.assumedThickness;

    uniforms.maxScreenSpaceKernelRadius.value = this.aoMaxScreenSpaceKernelRadius;
    uniforms.aoIntensity.value = this.aoIntensity;

    uniforms.depthInvSize.value.set(1.0 / this.depthNormalPass.paddedSize.x, 1.0 / this.depthNormalPass.paddedSize.y);
    uniforms.gtaoWorldSpaceScale.value = this.aoWorldSpaceScale;
    uniforms.gtaoFalloffMinRelativeRadius.value = this.aoFalloffMinRelativeRadius;
    uniforms.gtaoFalloffMaxRelativeRadius.value = this.aoFalloffMaxRelativeRadius;

    if (this.output === ScreenSpaceShadowPassOutput.WriteToScreen) {
      renderer.setRenderTarget(writeBuffer);
      renderer.clear();
      this.fsQuad.render(renderer);
    } else {
      renderer.setRenderTarget(this.shadowBuffer);
      renderer.clear();
      this.fsQuad.render(renderer);
    }

    // Restore renderer settings
    renderer.setClearColor(this._prevClearColor, prevClearAlpha);
    renderer.autoClear = prevAutoClear;
  }

  private updateOutputMaterial() {
    this.outputMaterial.defines.NUM_SHADOW_STEPS = this._shadowsEnabled ? Math.floor(this._shadowSteps) : 0;
    this.outputMaterial.defines.NUM_AO_SAMPLES = this._aoEnabled ? Math.floor(this._aoSamples) : 0;
    this.outputMaterial.defines.NUM_AO_ANGLE_SAMPLES = Math.floor(this._aoAngleSamples);
    this.outputMaterial.defines.AO_COSINE_WEIGHTING = this._aoCosineWeighting ? 1 : 0;
    this.outputMaterial.needsUpdate = true;
  }

  getOutputMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      name: "ScreenSpaceShadowPass outputMaterial",
      defines: {
        ...screenSpaceViewPositionDefineDefaults,
        ...screenSpaceViewNormalDefineDefaults,
        MAX_STEPS: 5,

        AO_COSINE_WEIGHTING: 0,
        NUM_AO_ANGLE_SAMPLES: 2,
        NUM_AO_SAMPLES: 2,
      },

      uniforms: THREE.UniformsUtils.clone({
        ...screenSpaceViewPositionUniformDefaults,
        ...screenSpaceViewNormalUniformDefaults,
        ...gtaoCommonUniformDefaults,

        lightDirection: { value: new THREE.Vector3() }, // in view space, direction from target to light position.
        stepLength: { value: 0.3 },

        resolution: { value: new THREE.Vector2() },
        assumedThickness: { value: 1 },
        randomSeed: { value: 0 },
        maxDistance: { value: 100 },

        maxScreenSpaceKernelRadius: { value: 0.1 },
        aoIntensity: { value: 0.8 },
      }),

      vertexShader: UVVertexShader,
      fragmentShader: `
        #include <common>
        #include <packing>

        ${screenSpaceViewNormalGLSL}
        ${screenSpaceViewPositionGLSL}

        varying vec2 vUv;

        uniform float assumedThickness;

        uniform vec2 resolution;

        uniform vec3 lightDirection;
        uniform float stepLength;

        uniform float randomSeed;
        uniform float maxDistance;

        uniform float maxScreenSpaceKernelRadius;
        uniform float aoIntensity;

        ${gtaoCommonUniformGLSL}
        ${gtaoHelpersGLSL}

        // Returns a higher number if the pixel is more in shadow.
        float getSoftShadowFactor(float z, vec2 rayPixelCoords, bool xInterpolate) {
          vec2 floorPixelCoords = floor(rayPixelCoords - 0.5) + 0.5;
          vec2 ceilPixelCoords = floorPixelCoords + 1.0;

          vec2 uv0;
          vec2 uv1;
          float t;
          if (xInterpolate) {
            uv0 = vec2(floorPixelCoords.x, rayPixelCoords.y);
            uv1 = vec2(ceilPixelCoords.x, rayPixelCoords.y);
            t = rayPixelCoords.x - floorPixelCoords.x;
          } else {
            uv0 = vec2(rayPixelCoords.x, floorPixelCoords.y);
            uv1 = vec2(rayPixelCoords.x, ceilPixelCoords.y);
            t = rayPixelCoords.y - floorPixelCoords.y;
          }

          uv0 /= resolution;
          uv1 /= resolution;

          float depth0 = GET_DEPTH(tDepth, uv0);
          float depth1 = GET_DEPTH(tDepth, uv1);

          if (IS_ANY_DEPTH_OUT_OF_BOUNDS(depth0, depth1)) {
            return 1000.0;
          }

          float viewZ0 = getViewZ(depth0);
          float viewZ1 = getViewZ(depth1);

          float sceneZNear = mix(viewZ0, viewZ1, t);
          float sceneZFar = sceneZNear - assumedThickness;

          if (z < sceneZFar) {
            return 1000.0;
          }

          return z - sceneZNear;
        }

        bool isOutsidePixelBounds(vec2 pixelCoords) {
          return any(lessThan(pixelCoords, vec2(0.5))) || any(greaterThan(pixelCoords, resolution - 0.5));
        }

        float getVisibility(const in vec3 centerViewPosition, const in vec3 centerViewNormal) {
          vec2 centerUv = vUv;
          float checkerboard = mod(floor(gl_FragCoord.x) + floor(gl_FragCoord.y), 2.0);

          INIT_Y_RELATIVE_RADIUS_FROM_WORLD_SPACE(originalRadius, gtaoWorldSpaceScale, centerViewPosition.z, cameraProjectionMatrix);
          originalRadius = min(originalRadius, maxScreenSpaceKernelRadius);
          originalRadius *= INV_NUM_AO_SAMPLES;
          float radiusStep = originalRadius;

          float baseAngle = (checkerboard + rand( vUv + randomSeed )) * 0.5;

          #if NUM_AO_ANGLE_SAMPLES == 1
          // Adding a random term to radius makes the effect converge to a nicer result after TAA.
          // In our case the used radius is small, but the difference can still be noticeable on small sample counts.
          originalRadius += radiusStep * ( fract( baseAngle * 317.0 ) - 0.5 );
          #endif

          // Radius must be at least so high that the same pixel can't be sampled twice from the depth buffer.
          originalRadius = max(originalRadius, depthInvSize.y);

          ${gtaoAcculumateSlicesGLSL(`
            float sampleDepth1 = GET_DEPTH(tDepth, sampleUv1);
            float sampleDepth2 = GET_DEPTH(tDepth, sampleUv2);
          `)}

          return visibility / totalWeight;
        }

        void main() {
          vec3 centerNormal = getViewNormal(vUv);
          float lightDotNormal = dot(lightDirection, centerNormal);

          float minSoftShadowFactor = 1.0;

          bool skipShadow = false;
          if (lightDotNormal < 0.0) {
            // Surface is not facing the light.
            skipShadow = true;
            minSoftShadowFactor = 0.0;
          }

          float centerDepth = GET_DEPTH(tDepth, vUv);
          if (IS_DEPTH_OUT_OF_BOUNDS(centerDepth)) {
            gl_FragColor = vec4(1.0);
            return;
          }

          float centerViewZ = getViewZ(centerDepth);
          vec3 centerViewPos = getViewPosition(vUv, centerViewZ);
          #if PERSPECTIVE_CAMERA == 1
          float centerClipW = cameraProjectionMatrix[2][3] * centerViewZ + cameraProjectionMatrix[3][3];
          #else
          float centerClipW = 1.0;
          #endif

          vec3 endViewPos = centerViewPos + lightDirection * 20.0;

#if NUM_SHADOW_STEPS > 0
          vec4 H1 = cameraProjectionMatrix * vec4(endViewPos, 1.0);

          float k0 = 1.0 / centerClipW;
          float k1 = 1.0 / H1.w;

          vec3 Q0 = centerViewPos * k0;
          vec3 Q1 = endViewPos * k1;

          vec2 P0 = vUv * resolution;
          vec2 P1 = (H1.xy * k1 * 0.5 + 0.5) * resolution;

          if (distance(P0, P1) < 0.05) {
            skipShadow = true;
          }

          if (!skipShadow) {
            // Derivatives
            vec2 dP = P1 - P0;
            float greaterDelta = dP.x;

            // In case we're taking 1-pixel steps in the y direction, then we interpolate depth samples in the x direction.
            bool xInterpolate = false;
            if (abs(dP.y) > abs(dP.x)) {
              greaterDelta = dP.y;
              xInterpolate = true;
            }
            vec3 dQ = Q1 - Q0;
            float dk = k1 - k0;

            // Track all values in a vector
            vec4 PQK = vec4(P0, Q0.z, k0);
            vec4 dPQK = vec4(dP, dQ.z, dk) / abs(greaterDelta); // 1 pixel step

            float prevZ = PQK.z / PQK.w;

            // Rendering soft shadows on a spherical mesh is a good way to test the constants affecting bias.
            float bias = min(lightDotNormal * 1.2 + 0.1, 1.0) - 1.0;

            bool inShadow = false;
            for (int i = 0; i < NUM_SHADOW_STEPS; ++i) {
              PQK += dPQK;
              if (isOutsidePixelBounds(PQK.xy)) {
                break;
              }
              float testZ = PQK.z / PQK.w;
              float dZ = abs(testZ - prevZ);
              float softShadowFactor = getSoftShadowFactor(testZ, PQK.xy, xInterpolate) / (dZ + 0.001) - bias;
              minSoftShadowFactor = min(softShadowFactor, minSoftShadowFactor);
              prevZ = testZ;
            }
            minSoftShadowFactor = max(minSoftShadowFactor, 0.0);
          }
#endif // NUM_SHADOW_STEPS > 0

#if NUM_AO_SAMPLES > 0
          float aoVisibility = mix(1.0, getVisibility(centerViewPos, centerNormal), aoIntensity);
#else
          float aoVisibility = 1.0;
#endif

          gl_FragColor = vec4(minSoftShadowFactor, aoVisibility, 0.0, 1.0);
        }
        `,
    });
  }
}
